/**
 * Геометрия плавающей кнопки колеса и «один раз в день» для окна розыгрыша.
 *
 * Вынесено из компонента отдельными функциями без DOM: так их можно проверить
 * обычным Node-тестом (см. отчёт батча) и случайно не сломать перетаскивание.
 */

export const FAB_SIZE = 54
export const FAB_MARGIN = 14

// Сколько пикселей палец может «съехать» при тапе, оставаясь тапом.
// Прежние 5 px были слишком строгими: в Telegram палец почти всегда
// сдвигается на 6–10 px, такой тап считался перетаскиванием, кнопка
// молча прилипала к краю и окно не открывалось.
export function tapSlop(pointerType) {
  return pointerType === 'mouse' ? 5 : 10
}

/** Ушёл ли указатель достаточно далеко, чтобы считать жест перетаскиванием. */
export function isDragMove(dx, dy, slop = 10) {
  const limit = Math.max(1, Number(slop) || 10)
  return Math.hypot(Number(dx) || 0, Number(dy) || 0) >= limit
}

// Пауза, в течение которой «хвостовой» клик после тапа/перетаскивания
// игнорируется — иначе он может повторно открыть или сразу закрыть окно.
export const CLICK_GUARD_MS = 400

/**
 * Можно ли закрыть окно кликом по подложке.
 *
 * Клик должен быть именно по подложке (не по окну), окно не должно быть
 * занято оплатой, и с момента открытия должна пройти защитная пауза —
 * иначе «хвост» того же тапа, которым открыли окно, закроет его сразу.
 */
export function canCloseOnBackdrop({ busy, sameTarget, elapsedMs }) {
  if (busy) return false
  if (sameTarget !== true) return false
  return Number(elapsedMs) >= CLICK_GUARD_MS
}

const EDGES = ['left', 'right', 'top', 'bottom']

export function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v))
}

// Локальная дата устройства: «один раз в день» — это день юзера, а не UTC.
export function localDayKey(date = new Date()) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/**
 * Точка кнопки на краю экрана. ratio 0..1 — положение вдоль края
 * (0 — начало, 1 — конец), поэтому позиция переживает смену размера экрана.
 */
export function edgePoint(edge, ratio, width, height, size = FAB_SIZE, margin = FAB_MARGIN) {
  const r = clamp(Number(ratio) || 0, 0, 1)
  const alongX = Math.max(0, width - margin * 2 - size)
  const alongY = Math.max(0, height - margin * 2 - size)
  const maxX = Math.max(margin, width - margin - size)
  const maxY = Math.max(margin, height - margin - size)

  switch (edge) {
    case 'left':   return { x: margin, y: clamp(margin + r * alongY, margin, maxY) }
    case 'top':    return { x: clamp(margin + r * alongX, margin, maxX), y: margin }
    case 'bottom': return { x: clamp(margin + r * alongX, margin, maxX), y: maxY }
    default:       return { x: maxX, y: clamp(margin + r * alongY, margin, maxY) }
  }
}

/**
 * Притягивает брошенную кнопку к ближайшему краю и возвращает и точку,
 * и компактное описание позиции для localStorage.
 */
export function snapToEdge(x, y, width, height, size = FAB_SIZE, margin = FAB_MARGIN) {
  const px = clamp(Number(x) || 0, margin, Math.max(margin, width - margin - size))
  const py = clamp(Number(y) || 0, margin, Math.max(margin, height - margin - size))

  const distances = {
    left: px,
    right: width - size - px,
    top: py,
    bottom: height - size - py,
  }

  let edge = 'right'
  let best = Infinity
  for (const id of EDGES) {
    if (distances[id] < best) { best = distances[id]; edge = id }
  }

  const alongX = Math.max(1, width - margin * 2 - size)
  const alongY = Math.max(1, height - margin * 2 - size)
  const ratio = edge === 'left' || edge === 'right'
    ? clamp((py - margin) / alongY, 0, 1)
    : clamp((px - margin) / alongX, 0, 1)

  const point = edgePoint(edge, ratio, width, height, size, margin)
  return { edge, ratio, x: point.x, y: point.y }
}

/** Читает сохранённую позицию; мусор и старые форматы заменяет значением по умолчанию. */
export function parsePlace(raw, fallback = { edge: 'right', ratio: 0.55 }) {
  try {
    const obj = typeof raw === 'string' ? JSON.parse(raw) : raw
    if (!obj || typeof obj !== 'object') return fallback
    const edge = EDGES.includes(obj.edge) ? obj.edge : fallback.edge
    return { edge, ratio: clamp(Number(obj.ratio) || 0, 0, 1) }
  } catch {
    return fallback
  }
}
